import React from 'react';
import { X, CheckCircle, XCircle } from '@phosphor-icons/react';
import './JournalEntryLineItems.css';

/**
 * Specialized component for managing journal entry line items
 * @param {array} items - Line items array
 * @param {array} accounts - Chart of Accounts for dropdown
 * @param {function} onAddItem - Add line item handler
 * @param {function} onRemoveItem - Remove line item handler (index)
 * @param {function} onChangeItem - Change line item handler (index, field, value)
 */
const JournalEntryLineItems = ({
  items = [],
  accounts = [],
  onAddItem,
  onRemoveItem,
  onChangeItem,
}) => {
  // Calculate totals
  const totalDebits = items.reduce((sum, item) => sum + (parseFloat(item.debit_amount) || 0), 0);
  const totalCredits = items.reduce((sum, item) => sum + (parseFloat(item.credit_amount) || 0), 0);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const handleAccountChange = (index, accountId) => {
    onChangeItem(index, 'gl_account_id', accountId);
  };

  const getAccountDisplay = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? `${account.account_code} - ${account.account_name}` : 'Select Account';
  };

  return (
    <div className="journal-line-items">
      <h4>Line Items</h4>
      <div className="line-items-scroll">
        <table className="line-items-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Debit (GHS)</th>
              <th>Credit (GHS)</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>
                  <select
                    value={item.gl_account_id || ''}
                    onChange={(e) => handleAccountChange(idx, e.target.value)}
                    required
                    style={{ width: '100%', padding: '4px' }}
                  >
                    <option value="">Select Account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_code} - {account.account_name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={item.debit_amount || ''}
                    onChange={(e) => onChangeItem(idx, 'debit_amount', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={item.credit_amount || ''}
                    onChange={(e) => onChangeItem(idx, 'credit_amount', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => onChangeItem(idx, 'description', e.target.value)}
                  />
                </td>
                <td>
                  {items.length > 2 && (
                    <button
                      type="button"
                      onClick={() => onRemoveItem(idx)}
                      className="btn-remove"
                      title="Remove line item"
                    >
                      <X size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="line-items-footer">
        <div className="totals">
          <div className="total-item">
            <span>Debits:</span>
            <span className="total-amount">GHS {totalDebits.toFixed(2)}</span>
          </div>
          <div className="total-item">
            <span>Credits:</span>
            <span className="total-amount">GHS {totalCredits.toFixed(2)}</span>
          </div>
          <div className={`total-item status ${isBalanced ? 'balanced' : 'unbalanced'}`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {isBalanced ? <><CheckCircle size={14} /> Balanced</> : <><XCircle size={14} /> Unbalanced</>}
            </span>
          </div>
        </div>

        <button type="button" onClick={onAddItem} className="btn-add-item">
          + Add Line Item
        </button>
      </div>
    </div>
  );
};

export default JournalEntryLineItems;
